import { useRouter } from 'next/router';
import { useEffect, useRef, useState } from 'react';

import { Nullable } from '@/common/type/CommonTypes';
import { EventTag } from '@/common/type/GoogleTagType';
import { CouponNewUserSurveyGtmExitType, CouponNewUserSurveyGtmRole } from '@/core/common/enum/coupon/CouponNewUserSurveyEnum';
import {
  createSurveySessionId,
  getSurveyGtmDevice,
  getSurveyGtmEntryPoint,
  getSurveyGtmPageName,
} from '@/helpers/coupon/CouponNewUserSurveyHelper';
import { useProfile } from '@/helpers/use';
import { UseTrackCouponEntryParams } from '@/hooks/coupon/type/useTrackCouponEntryType';
import { useGtmV2 } from '@/hooks/dataTracking/useGtmV2';

const useTrackCouponEntry = ({ isShowCoupon }: UseTrackCouponEntryParams) => {
  const { profile } = useProfile();
  const { asPath, pathname } = useRouter();
  const { sendGtmCouponSurveyEvent } = useGtmV2();
  const [surveySessionId, setSurveySessionId] = useState('');
  const [sessionEntryPoint, setSessionEntryPoint] = useState(getSurveyGtmEntryPoint(pathname));
  const exitTypeRef = useRef(CouponNewUserSurveyGtmExitType.CloseButton);
  const entryViewSentForPathRef = useRef<Nullable<string>>(null);
  const role = profile ? CouponNewUserSurveyGtmRole.Member : CouponNewUserSurveyGtmRole.Guest;

  // coupon_entry_view: once per page view, only while the tag is rendered.
  useEffect(() => {
    if (!isShowCoupon || entryViewSentForPathRef.current === asPath) {
      return;
    }

    entryViewSentForPathRef.current = asPath;
    sendGtmCouponSurveyEvent(EventTag.COUPON_ENTRY_VIEW, {
      role,
      entry_point: getSurveyGtmEntryPoint(pathname),
      page_name: getSurveyGtmPageName(pathname),
      device: getSurveyGtmDevice(),
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [asPath, isShowCoupon]);

  // Opening the modal starts a new survey session.
  const trackEntryClick = () => {
    const nextSurveySessionId = createSurveySessionId();
    const entryPoint = getSurveyGtmEntryPoint(pathname);
    setSurveySessionId(nextSurveySessionId);
    setSessionEntryPoint(entryPoint);
    exitTypeRef.current = CouponNewUserSurveyGtmExitType.CloseButton;
    sendGtmCouponSurveyEvent(EventTag.COUPON_ENTRY_CLICK, {
      role,
      entry_point: entryPoint,
      survey_session_id: nextSurveySessionId,
    });
  };

  // MUI calls this before closing the dialog; the close button and Esc keep close_button.
  const handleBackdropClick = () => {
    exitTypeRef.current = CouponNewUserSurveyGtmExitType.OverlayClick;
  };

  return {
    gtmSession: { surveySessionId, entryPoint: sessionEntryPoint, exitType: exitTypeRef.current },
    trackEntryClick,
    handleBackdropClick,
  };
};

export default useTrackCouponEntry;
